import { useCallback, useEffect, useState } from "react";
import { Plus, Star, Pencil, Trash2, Warehouse as WarehouseIcon, X } from "lucide-react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", contact_name: "", address: "", city: "", state: "", pincode: "", phone: "", email: "", gst: "", operating_hours: "09:00 - 18:00", is_default: false };
const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-shipany-navy placeholder:text-slate-400 focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15";

export default function Warehouses() {
  const [warehouses, setWarehouses] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/warehouses").then(({ data }) => setWarehouses(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (w) => { setEditing(w); setForm({ ...EMPTY, ...w }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = editing ? await api.put(`/warehouses/${editing.warehouse_id}`, form) : await api.post("/warehouses", form);
      toast.success(editing ? "Warehouse updated" : "Warehouse created");
      if (data?.sync_warning)
        toast.warning("Warehouse saved, but pickup registration is still pending with a delivery partner. If booking fails from this warehouse, edit and re-save it or contact support.", { duration: 9000 });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"?`)) return;
    await api.delete(`/warehouses/${w.warehouse_id}`);
    toast.success("Warehouse deleted");
    load();
  };

  return (
    <div data-testid="warehouses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Warehouses</h1>
          <p className="text-sm text-slate-500">Pickup locations for your shipments.</p>
        </div>
        <button onClick={openNew} data-testid="warehouse-create-btn" className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] glow-blue">
          <Plus className="h-4 w-4" /> Add Warehouse
        </button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="warehouses-grid">
        {(warehouses || []).map((w) => (
          <div key={w.warehouse_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`warehouse-card-${w.warehouse_id}`}>
            <div className="flex items-start justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-blue/10 text-shipany-blue"><WarehouseIcon className="h-5 w-5" /></span>
              <span className="flex items-center gap-1.5">
                {w.serial && <span className="mono rounded-full bg-shipany-blue/10 px-2.5 py-1 text-[10px] font-bold text-shipany-blue" data-testid={`warehouse-serial-${w.warehouse_id}`}>WH-{w.serial}</span>}
                {w.is_default && <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-600"><Star className="h-3 w-3" /> DEFAULT</span>}
              </span>
            </div>
            <h3 className="font-display mt-3 font-bold text-shipany-navy">{w.name}</h3>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{w.address}, {w.city}, {w.state} — <span className="mono">{w.pincode}</span></p>
            <p className="mt-2 text-xs text-slate-400">{w.contact_name} · {w.phone}</p>
            {w.gst && <p className="mono mt-1 text-xs text-slate-400">GST {w.gst}</p>}
            <p className="mt-1 text-xs text-slate-400">{w.operating_hours}</p>
            {w.sync_warning && <p className="mt-2 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-medium text-amber-700" data-testid={`warehouse-sync-warning-${w.warehouse_id}`}>Pickup registration pending with a delivery partner — edit &amp; re-save (use a unique name) or contact support.</p>}
            <div className="mt-4 flex gap-2">
              <button onClick={() => openEdit(w)} data-testid={`warehouse-edit-${w.warehouse_id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Pencil className="h-3 w-3" /> Edit</button>
              <button onClick={() => remove(w)} data-testid={`warehouse-delete-${w.warehouse_id}`} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-red-500 hover:bg-red-50"><Trash2 className="h-3 w-3" /> Delete</button>
            </div>
          </div>
        ))}
        {warehouses && warehouses.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-400">
            No warehouses yet — add your first pickup location.
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="warehouse-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <form onSubmit={save} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-shipany-navy">{editing ? "Edit Warehouse" : "New Warehouse"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
              <div><label className="text-xs font-semibold text-slate-500">Warehouse name *</label>
                <input data-testid="wh-name" className={`${inputCls} mt-1`} placeholder="e.g. Main Godown" minLength={3} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Contact person</label>
                <input data-testid="wh-contact" className={`${inputCls} mt-1`} placeholder="e.g. Ramesh Kumar" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="text-xs font-semibold text-slate-500">Full address *</label>
                <input data-testid="wh-address" className={`${inputCls} mt-1`} placeholder="Include house/plot no., e.g. Plot 21, Udyog Vihar" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">City *</label>
                <input data-testid="wh-city" className={`${inputCls} mt-1`} placeholder="e.g. Gurugram" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">State</label>
                <input data-testid="wh-state" className={`${inputCls} mt-1`} placeholder="e.g. Haryana" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Pincode *</label>
                <input data-testid="wh-pincode" className={`${inputCls} mono mt-1`} placeholder="6-digit" maxLength={6} required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Mobile number *</label>
                <input data-testid="wh-phone" className={`${inputCls} mono mt-1`} placeholder="10-digit" required inputMode="numeric" maxLength={10} pattern="[6-9][0-9]{9}"
                  title="10-digit Indian mobile number (required by courier partners)"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                <p className="mt-1 text-[10px] text-slate-400">10-digit Indian mobile — required by courier partners (Shiprocket) for pickup verification.</p>
              </div>
              <div><label className="text-xs font-semibold text-slate-500">GSTIN</label>
                <input data-testid="wh-gst" className={`${inputCls} mono mt-1`} placeholder="e.g. 06AABCA1234F1Z5" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-slate-500">Operating hours</label>
                <input data-testid="wh-hours" className={`${inputCls} mt-1`} placeholder="e.g. 9 AM – 7 PM" value={form.operating_hours} onChange={(e) => setForm({ ...form, operating_hours: e.target.value })} /></div>
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600 sm:col-span-2">
                <input type="checkbox" data-testid="wh-default" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 rounded border-slate-300 text-shipany-blue" />
                Set as default pickup warehouse
              </label>
            </div>
            <button data-testid="wh-save-btn" disabled={saving} className="mt-6 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update Warehouse" : "Create Warehouse"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
